import React, { useEffect, useState, useMemo } from "react";
import {
  Activity,
  Plus,
  Phone,
  Mail,
  MessageSquare,
  Footprints,
  Users as UsersIcon,
  Sparkles,
  PartyPopper,
  StickyNote,
  Pencil,
  Trash2,
  X,
  Check,
} from "lucide-react";
import {
  fetchInteractions,
  createInteraction,
  updateInteraction,
  deleteInteraction,
  fetchDispensaryContacts,
} from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

const TYPE_META = {
  call:          { label: "Call",          icon: Phone,         color: "text-emerald-300", bg: "bg-emerald-500/15 border-emerald-500/30" },
  email:         { label: "Email",         icon: Mail,          color: "text-sky-300",     bg: "bg-sky-500/15 border-sky-500/30" },
  text:          { label: "Text",          icon: MessageSquare, color: "text-cyan-300",    bg: "bg-cyan-500/15 border-cyan-500/30" },
  visit:         { label: "Visit",         icon: Footprints,    color: "text-violet-300",  bg: "bg-violet-500/15 border-violet-500/30" },
  meeting:       { label: "Meeting",       icon: UsersIcon,     color: "text-amber-300",   bg: "bg-amber-500/15 border-amber-500/30" },
  demo:          { label: "Demo",          icon: Sparkles,      color: "text-fuchsia-300", bg: "bg-fuchsia-500/15 border-fuchsia-500/30" },
  tasting_event: { label: "Bus Event", icon: PartyPopper,   color: "text-pink-300",    bg: "bg-pink-500/15 border-pink-500/30" },
  note:          { label: "Note",          icon: StickyNote,    color: "text-zinc-300",    bg: "bg-zinc-500/15 border-zinc-500/30" },
};

const OUTCOMES = [
  { value: "",                 label: "—" },
  { value: "connected",        label: "Connected" },
  { value: "voicemail",        label: "Voicemail" },
  { value: "no_answer",        label: "No Answer" },
  { value: "email_sent",       label: "Email Sent" },
  { value: "email_replied",    label: "Email Replied" },
  { value: "meeting_booked",   label: "Meeting Booked" },
  { value: "sample_dropped",   label: "Sample Dropped" },
  { value: "demo_completed",   label: "Demo Completed" },
  { value: "order_placed",     label: "Order Placed" },
  { value: "lost",             label: "Lost" },
  { value: "follow_up_needed", label: "Follow-up Needed" },
  { value: "other",            label: "Other" },
];

const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.map((o) => [o.value, o.label]));

const TYPES = Object.entries(TYPE_META).map(([value, m]) => ({ value, label: m.label }));

function nowDateTimeLocal() {
  // datetime-local input wants `YYYY-MM-DDTHH:MM` (no seconds, no tz).
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalInput(iso) {
  if (!iso) return nowDateTimeLocal();
  const d = new Date(iso);
  if (isNaN(d)) return nowDateTimeLocal();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local) {
  // Convert "YYYY-MM-DDTHH:MM" (interpreted as local time) → ISO with TZ
  if (!local) return null;
  const d = new Date(local);
  return isNaN(d) ? null : d.toISOString();
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const delta = Date.now() - then;
  if (delta < 0) return new Date(iso).toLocaleString();
  const mins = Math.round(delta / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const EMPTY = {
  type: "call",
  occurred_at: nowDateTimeLocal(),
  contact_id: "",
  duration_minutes: "",
  outcome: "",
  notes: "",
};

/**
 * Chronological activity log for a single dispensary account. Each entry
 * is a "touchpoint" — call, email, visit, etc. — captured with a free-text
 * note, optional duration + outcome, and an optional link to a specific
 * `DispensaryContact` so we know which person was involved.
 *
 * No user attribution yet — will be added when Phase B.5 (auth) lands.
 */
export function InteractionTimeline({ dispensaryId }) {
  const [items, setItems] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // id | "new" | null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    if (!dispensaryId) return;
    try {
      const [its, cts] = await Promise.all([
        fetchInteractions(dispensaryId),
        fetchDispensaryContacts(dispensaryId).catch(() => []),
      ]);
      setItems(its);
      setContacts(cts);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    reload();
    // Listen for cross-component refresh signals (e.g. the Tasks card
    // dispatches this after auto-logging an Interaction on completion).
    const onChange = () => reload();
    window.addEventListener("jacktrack:interactions-changed", onChange);
    return () =>
      window.removeEventListener("jacktrack:interactions-changed", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensaryId]);

  const contactName = useMemo(() => {
    const m = {};
    for (const c of contacts || []) m[c.id] = c.name;
    return m;
  }, [contacts]);

  const startNew = () => {
    setForm({ ...EMPTY, occurred_at: nowDateTimeLocal() });
    setEditing("new");
  };

  const startEdit = (it) => {
    setForm({
      type: it.type || "call",
      occurred_at: toLocalInput(it.occurred_at),
      contact_id: it.contact_id || "",
      duration_minutes: it.duration_minutes ?? "",
      outcome: it.outcome || "",
      notes: it.notes || "",
    });
    setEditing(it.id);
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.notes.trim() && form.type === "note") {
      toast.error("Note text is required for a Note entry");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        type: form.type,
        occurred_at: fromLocalInput(form.occurred_at),
        contact_id: form.contact_id || null,
        duration_minutes:
          form.duration_minutes === "" ? null : Number(form.duration_minutes),
        outcome: form.outcome || null,
        notes: form.notes.trim(),
      };
      if (editing === "new") {
        await createInteraction(dispensaryId, payload);
        toast.success("Activity logged");
      } else {
        await updateInteraction(editing, payload);
        toast.success("Activity updated");
      }
      cancel();
      reload();
    } catch (e) {
      toast.error("Could not save activity", { description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete this ${TYPE_META[it.type]?.label.toLowerCase() || "activity"} log?`)) return;
    try {
      await deleteInteraction(it.id);
      toast.success("Activity deleted");
      reload();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="panel p-5" data-testid="interaction-timeline">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono">
            <Activity className="w-3 h-3 text-emerald-300" />
            Activity timeline
          </div>
          <p className="text-xs text-zinc-500 mt-1.5 max-w-xl leading-relaxed">
            Every call, email, visit, demo, and bus event logged
            against this account. The most recent activity appears at the
            top.
          </p>
        </div>
        {editing !== "new" && (
          <Button
            data-testid="add-interaction-btn"
            onClick={startNew}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md text-xs"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Log activity
          </Button>
        )}
      </div>

      {editing === "new" && (
        <InteractionForm
          form={form}
          setForm={setForm}
          contacts={contacts}
          onSave={save}
          onCancel={cancel}
          saving={saving}
          mode="new"
        />
      )}

      {items === null ? (
        <div className="px-3 py-6 text-xs text-zinc-500">Loading…</div>
      ) : items.length === 0 && editing !== "new" ? (
        <div className="px-4 py-10 text-center text-xs text-zinc-500 border border-dashed border-zinc-800 rounded-md">
          <Activity className="w-6 h-6 mx-auto text-zinc-700 mb-2" />
          No activity logged yet at this account.
          <div className="mt-1 text-[10px] text-zinc-600">
            Log a call, email, or visit to start building the timeline.
          </div>
        </div>
      ) : (
        <div className="relative">
          {/* Vertical timeline rail */}
          <div className="absolute left-[15px] top-1 bottom-1 w-px bg-zinc-800 pointer-events-none" />
          <div className="space-y-2.5">
            {items.map((it) =>
              editing === it.id ? (
                <InteractionForm
                  key={it.id}
                  form={form}
                  setForm={setForm}
                  contacts={contacts}
                  onSave={save}
                  onCancel={cancel}
                  saving={saving}
                  mode="edit"
                />
              ) : (
                <InteractionRow
                  key={it.id}
                  it={it}
                  contactName={contactName[it.contact_id]}
                  onEdit={() => startEdit(it)}
                  onDelete={() => remove(it)}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InteractionRow({ it, contactName, onEdit, onDelete }) {
  const meta = TYPE_META[it.type] || TYPE_META.note;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`interaction-row-${it.id}`}
      className="group relative pl-9 border border-zinc-800 rounded-md p-3 bg-zinc-950/40 hover:bg-zinc-900/40 transition-colors"
    >
      {/* Timeline dot */}
      <div
        className={`absolute left-[3px] top-3.5 w-6 h-6 rounded-full border flex items-center justify-center ${meta.bg}`}
      >
        <Icon className={`w-3 h-3 ${meta.color}`} />
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className={`font-medium ${meta.color}`}>{meta.label}</span>
            {it.outcome && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-800 rounded px-1.5 py-0.5">
                {OUTCOME_LABEL[it.outcome] || it.outcome}
              </span>
            )}
            {typeof it.duration_minutes === "number" && (
              <span className="font-mono text-[10px] text-zinc-500">
                {it.duration_minutes}m
              </span>
            )}
            {contactName && (
              <span className="font-mono text-[10px] text-zinc-400">
                w/ {contactName}
              </span>
            )}
            <span className="font-mono text-[10px] text-zinc-600 ml-auto">
              {relativeTime(it.occurred_at)}
            </span>
          </div>
          {it.notes && (
            <div className="mt-1.5 text-xs text-zinc-200 leading-relaxed whitespace-pre-wrap break-words">
              {it.notes}
            </div>
          )}
        </div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 shrink-0">
          <button
            data-testid={`edit-interaction-${it.id}`}
            onClick={onEdit}
            className="text-zinc-500 hover:text-emerald-300 p-1.5 rounded hover:bg-zinc-800"
            title="Edit"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            data-testid={`delete-interaction-${it.id}`}
            onClick={onDelete}
            className="text-zinc-500 hover:text-rose-400 p-1.5 rounded hover:bg-zinc-800"
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function InteractionForm({ form, setForm, contacts, onSave, onCancel, saving, mode }) {
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <div
      data-testid={mode === "new" ? "interaction-form-new" : "interaction-form-edit"}
      className="border border-emerald-500/30 bg-emerald-500/5 rounded-md p-3 mb-3 space-y-2"
    >
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 block mb-1">
            Type
          </label>
          <Select value={form.type} onValueChange={(v) => setField("type", v)}>
            <SelectTrigger
              data-testid="interaction-form-type"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              {TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 block mb-1">
            When
          </label>
          <Input
            data-testid="interaction-form-when"
            type="datetime-local"
            value={form.occurred_at}
            onChange={(e) => setField("occurred_at", e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-xs font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 block mb-1">
            Outcome
          </label>
          <Select
            value={form.outcome || "_none"}
            onValueChange={(v) => setField("outcome", v === "_none" ? "" : v)}
          >
            <SelectTrigger
              data-testid="interaction-form-outcome"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              <SelectItem value="_none">—</SelectItem>
              {OUTCOMES.filter((o) => o.value).map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 block mb-1">
            Duration (min)
          </label>
          <Input
            data-testid="interaction-form-duration"
            type="number"
            min="0"
            placeholder="optional"
            value={form.duration_minutes}
            onChange={(e) => setField("duration_minutes", e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-xs font-mono"
          />
        </div>
      </div>
      {contacts && contacts.length > 0 && (
        <div>
          <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 block mb-1">
            Person at account
          </label>
          <Select
            value={form.contact_id || "_none"}
            onValueChange={(v) => setField("contact_id", v === "_none" ? "" : v)}
          >
            <SelectTrigger
              data-testid="interaction-form-contact"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-xs"
            >
              <SelectValue placeholder="— Not tied to a specific person —" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              <SelectItem value="_none">— Not tied to a specific person —</SelectItem>
              {contacts.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.role ? ` · ${c.role.replace(/_/g, " ")}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <Textarea
        data-testid="interaction-form-notes"
        placeholder="What happened? Any next steps?"
        rows={3}
        value={form.notes}
        onChange={(e) => setField("notes", e.target.value)}
        className="bg-zinc-950 border-zinc-800 text-zinc-100 text-xs font-mono"
      />
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-zinc-100 rounded-md text-xs h-8"
        >
          <X className="w-3.5 h-3.5 mr-1" />
          Cancel
        </Button>
        <Button
          data-testid={mode === "new" ? "interaction-save-new" : "interaction-save-edit"}
          size="sm"
          onClick={onSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md text-xs h-8"
        >
          <Check className="w-3.5 h-3.5 mr-1" />
          {saving ? "Saving…" : mode === "new" ? "Log activity" : "Save"}
        </Button>
      </div>
    </div>
  );
}

export default InteractionTimeline;
